import { DashboardColumnStatus, StatusValue } from '@/shared';
import {
  convertStatusIntoDashboardStatus,
  convertTimeSecondsToString,
} from '@/utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { Tooltip } from '../Tooltip';
import { hourWidgetStyling } from './styles';

interface HourWidgetProps {
  analysisSeconds: number;
  estimatedSeconds: number;
  usedSeconds: number;
  status: StatusValue;
}

export const HourWidget = ({
  analysisSeconds,
  estimatedSeconds,
  usedSeconds,
  status,
}: HourWidgetProps) => {
  const { t } = useTranslation();

  const dashboardStatus = useMemo(
    () => convertStatusIntoDashboardStatus(status),
    [status],
  );

  const billableSeconds = useMemo(() => {
    return usedSeconds + analysisSeconds;
  }, [analysisSeconds, usedSeconds]);

  const { className, icon } = hourWidgetStyling(
    dashboardStatus,
    estimatedSeconds,
    usedSeconds,
  );

  const hourContent = useMemo(() => {
    if (dashboardStatus == DashboardColumnStatus.Submitted) {
      return;
    }

    if (dashboardStatus == DashboardColumnStatus.InEvaluation) {
      return analysisSeconds
        ? convertTimeSecondsToString(analysisSeconds)
        : undefined;
    }

    return convertTimeSecondsToString(billableSeconds ?? 0);
  }, [dashboardStatus, billableSeconds, analysisSeconds]);

  const tooltipContent = useMemo(() => {
    const hourInfo = [];

    if (dashboardStatus == DashboardColumnStatus.Submitted) {
      return;
    }

    switch (dashboardStatus) {
      case DashboardColumnStatus.InEvaluation:
        hourInfo.push({
          title: t('hour_analysis_time'),
          value: analysisSeconds,
        });
        break;
      case DashboardColumnStatus.InExecution:
        hourInfo.push({
          title: t('hour_estimated'),
          value: estimatedSeconds,
        });
        hourInfo.push({
          title: t('hour_execution'),
          value: usedSeconds,
        });
        break;
      case DashboardColumnStatus.Concluded:
        hourInfo.push({ title: t('hour_analysis'), value: analysisSeconds });
        hourInfo.push({ title: t('hour_estimated'), value: estimatedSeconds });
        hourInfo.push({ title: t('hour_execution_total'), value: usedSeconds });
        hourInfo.push({ title: t('hour_billable'), value: billableSeconds });
        break;
    }

    return (
      <div className="flex flex-col text-sm font-normal text-white">
        {hourInfo.map((info, index) => (
          <div key={index}>
            <span>{info.title}:</span>
            <span className="font-black">
              {convertTimeSecondsToString(info.value ?? 0, true)}
            </span>
          </div>
        ))}
      </div>
    );
  }, [
    dashboardStatus,
    t,
    analysisSeconds,
    estimatedSeconds,
    usedSeconds,
    billableSeconds,
  ]);

  return (
    hourContent && (
      <Tooltip enabled={!!tooltipContent} content={tooltipContent} showArrow>
        <div
          className={twMerge(
            'flex flex-row items-center gap-1 p-1 text-sm font-bold',
            className,
          )}
        >
          {icon}
          {hourContent}
        </div>
      </Tooltip>
    )
  );
};
